import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useExperience } from '../../contexts/ExperienceContext';
import { usageLevels, usageResponse } from '../../data/journeys';
import { brand } from '../../data/palette';
import { products } from '../../data/products';
import { Advance } from '../ui/Advance';
import { PillChoices } from '../ui/PillChoices';
import { Eyebrow, Stage, StageTitle } from '../ui/Stage';
const litCount: Record<string, number> = {
  one: 1,
  'two-three': 3,
  most: 5
};

/** Depth of current use, framed as discovery rather than deficiency. */
export function UsageStage() {
  const {
    signals,
    update,
    next,
    direction
  } = useExperience();
  const lit = signals.usage ? litCount[signals.usage] : 0;
  const accent = brand.lime;
  return <Stage direction={direction} className="min-h-screen py-28 sm:py-32" labelledBy="usage-title" data-id="element-353" data-mp="30mh53n6awfox">
      <div className="grid gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)] lg:items-center lg:gap-20" data-id="element-354" data-mp="upv1z0wicdf1">
        <div data-id="element-355" data-mp="11q0n8gt8ulnl">
          <Eyebrow accent={accent} data-id="element-356" data-mp="11pii0uolsx3j">Already using Filtek</Eyebrow>
          <StageTitle id="usage-title" size="display-sm" className="mt-7 max-w-lg" data-id="element-357" data-mp="11nxqzf1hzwaf">
            How much of Filtek are you actually using?
          </StageTitle>

          <div className="mt-10" data-id="element-358" data-mp="1beik4myek8ly">
            <PillChoices choices={usageLevels} selected={signals.usage} accent={accent} size="lg" onSelect={choice => update({
            usage: choice.id
          })} data-id="element-359" data-mp="1bhwq0569yzy5" />
          </div>

          <AnimatePresence mode="wait" data-id="element-360" data-mp="dsuv4n4a0skg">
            {signals.usage && <motion.div key={signals.usage} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }} className="mt-10 flex flex-col gap-9" data-id="element-361" data-mp="sp2jlzmpwg5j">
                <p className="max-w-lg text-lg font-light leading-relaxed text-enamel/85" data-id="element-362" data-mp="1z4wdqhwmyfin">
                  {usageResponse[signals.usage]}
                </p>
                <Advance label="Keep going" onClick={next} accent={accent} data-id="element-363" data-mp="2b761ytbln601" />
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* The family as stacked material layers */}
        <div className="space-y-2.5" data-id="element-364" data-mp="2l6537qu1bzd0">
          {products.slice(0, 5).map((product, index) => {
          const isLit = index < lit;
          return <motion.div key={product.id} className="relative overflow-hidden border border-enamel/10 px-5 py-4" initial={false} animate={{
            opacity: isLit ? 1 : 0.4
          }} transition={{
            duration: 0.6,
            delay: index * 0.09
          }} data-id="element-365" data-mp="1nga3xegjvekc" data-mpi={index}>
                <motion.span className="absolute inset-0 origin-left" style={{
              backgroundColor: product.accent
            }} initial={false} animate={{
              scaleX: isLit ? 1 : 0,
              opacity: isLit ? 0.16 : 0
            }} transition={{
              duration: 0.8,
              delay: index * 0.09,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-366" data-mp="14rvbcmql3shf" />
                <div className="relative flex items-center justify-between gap-4" data-id="element-367" data-mp="3ixjvn6sik49g">
                  <span className="text-base font-light text-enamel" data-id="element-368" data-mp="3e2eaia6xqtmf">{product.shortName}</span>
                  <span className="label-mono text-[9px] text-mute" data-id="element-369" data-mp="390yr0mkeov65">{product.descriptor}</span>
                </div>
              </motion.div>;
        })}
          <p className="pt-3 text-xs font-light text-mute" data-id="element-370" data-mp="2zacex2bwems7">
            Five solutions in one restorative family.
          </p>
        </div>
      </div>
    </Stage>;
}